import type { Board, Difficulty, Puzzle } from './types';
import { CELLS, SIZE, peers } from './board';
import { hasUniqueSolution, rate } from './solver';

/** Seeded PRNG (mulberry32). Deterministic for a given seed. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffle<T>(arr: T[], rng: () => number): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

const DIFFICULTY_RANK: Record<Difficulty, number> = {
  easy: 0,
  medium: 1,
  hard: 2,
  expert: 3,
};

/** Build a complete, valid, randomly-generated solved grid. */
function generateSolution(rng: () => number): Board {
  const board: Board = new Array(CELLS).fill(0);
  const fill = (): boolean => {
    let best = -1;
    let bestOptions: number[] | null = null;
    for (let i = 0; i < CELLS; i++) {
      if (board[i] !== 0) continue;
      const used = new Set<number>();
      for (const p of peers[i]!) {
        const v = board[p]!;
        if (v !== 0) used.add(v);
      }
      const options: number[] = [];
      for (let d = 1; d <= SIZE; d++) if (!used.has(d)) options.push(d);
      if (options.length === 0) return false;
      if (bestOptions === null || options.length < bestOptions.length) {
        best = i;
        bestOptions = options;
      }
    }
    if (best === -1) return true; // grid full
    for (const d of shuffle(bestOptions!, rng)) {
      board[best] = d;
      if (fill()) return true;
      board[best] = 0;
    }
    return false;
  };
  fill();
  return board;
}

/**
 * Remove cells from a solved grid while keeping a unique solution and never
 * exceeding the target difficulty. Returns the resulting givens.
 */
function carve(solution: Board, targetRank: number, rng: () => number): Board {
  const givens = [...solution];
  for (const i of shuffle([...Array(CELLS).keys()], rng)) {
    const backup = givens[i]!;
    if (backup === 0) continue;
    givens[i] = 0;
    if (!hasUniqueSolution(givens)) {
      givens[i] = backup; // removal broke uniqueness
      continue;
    }
    const rated = rate(givens);
    if (!rated || DIFFICULTY_RANK[rated.difficulty] > targetRank) {
      givens[i] = backup; // removal exceeded the target difficulty
    }
  }
  return givens;
}

/**
 * Generate a puzzle with a UNIQUE solution at approximately the requested
 * difficulty. Deterministic when `seed` is provided.
 */
export function generate(difficulty: Difficulty, seed?: number): Puzzle {
  const rng = mulberry32(seed ?? Math.floor(Math.random() * 0xffffffff));
  const targetRank = DIFFICULTY_RANK[difficulty];

  let best: Puzzle | null = null;
  const MAX_ATTEMPTS = 12;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const solution = generateSolution(rng);
    const givens = carve(solution, targetRank, rng);
    const rated = rate(givens)!; // carve preserves uniqueness, so this is non-null
    const puzzle: Puzzle = { givens, solution, difficulty: rated.difficulty };
    if (rated.difficulty === difficulty) return puzzle;
    // Keep the attempt closest to the target as a fallback.
    if (
      best === null ||
      Math.abs(DIFFICULTY_RANK[rated.difficulty] - targetRank) <
        Math.abs(DIFFICULTY_RANK[best.difficulty] - targetRank)
    ) {
      best = puzzle;
    }
  }
  return best!;
}
